import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import WatchLaterOutlined from '@mui/icons-material/WatchLaterOutlined';
import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import dayjs from 'dayjs';
import { GroupMembershipInput, Role, Visitor } from 'graphql/generated/graphqlTypes';
import useAddOrEditVisitor from 'hooks/allVisitors/drawers/useAddOrEditVisitor';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useController, useForm, useWatch } from 'react-hook-form';
import { getUniqueVisitorRolesWithoutInternalRolesAndExtGroupUsers, translateRole } from 'utils/Visitor/visitor';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import Loader from 'v2/components/Loader/Loader';
import useModalContext from 'v2/components/Modal/useModalContext';
import RepeatOverflowCounter from 'v2/components/RepeatOverflowCounter/RepeatOverflowCounter';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useAllVisitors from 'v2/hooks/useAllVisitors/useAllVisitors';
import { useSearch } from 'v2/hooks/useSearch/useSearch';
import { formatDate } from 'v2/utils/dateTime';
import { VisitorGroupAutocompleteOption } from 'v2/utils/visitorGroupAutocomplete';
import ExpirationDateModal from '../../../UserDetails/components/ExpirationDateModal';

interface Props {
  preSelectedGroup?: VisitorGroupAutocompleteOption | null;
  topContent?: ReactNode;
  isMaxNumberOfUsersReached?: boolean;
}

export interface AddExistingUserFormValues {
  visitorGroup: VisitorGroupAutocompleteOption | null;
  selectedUsers: Visitor[];
}

const FORM_ID = 'add-existing-user-drawer-form';
const VISITOR_SEARCH_KEYS = ['name', 'email'];

const UserFormDrawerExistingUser = ({ preSelectedGroup, topContent, isMaxNumberOfUsersReached }: Props) => {
  const theme = useTheme();
  const { translation } = useTranslation();
  const [filterStr, setFilterStr] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const { openModal } = useModalContext();
  const [keyManagerIds, setKeyManagerIds] = useState<Set<string>>(new Set());
  const [expirationByUserId, setExpirationByUserId] = useState<Record<string, string | null>>({});
  const [error, setError] = useState<string>();

  const { visitorGroupsLoading, addVisitorsToVisitorGroup } = useAddOrEditVisitor({
    translation,
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const { allVisitors } = useAllVisitors({
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const handleClose = () => setAnchorEl(null);

  const groupId = preSelectedGroup?.value;

  const filterableVisitors = useMemo(() => {
    if (!allVisitors) return [];
    if (!groupId) return allVisitors;
    return allVisitors.filter(
      (visitor) => !visitor.visitorGroupMembership.some((membership) => membership.visitorGroup.id === groupId)
    );
  }, [allVisitors, groupId]);

  const membersThatMayBeManagers = useSearch({
    data: filterableVisitors,
    search: filterStr,
    keys: VISITOR_SEARCH_KEYS
  });
  const open = Boolean(anchorEl);

  const defaultAddExistingUserFormValues: AddExistingUserFormValues = useMemo(
    () => ({
      visitorGroup: preSelectedGroup ?? null,
      selectedUsers: []
    }),
    [preSelectedGroup]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = useForm<AddExistingUserFormValues>({
    mode: 'onTouched',
    defaultValues: defaultAddExistingUserFormValues
  });

  const {
    field: { value: selectedUsers, onChange: onChangeSelectedUsers },
    fieldState: { error: selectedUsersError }
  } = useController({
    name: 'selectedUsers',
    control,
    rules: { validate: (value) => value.length > 0 || translation.select_at_least_one_user }
  });

  const selectedGroupOption = useWatch({ control, name: 'visitorGroup' });

  const handleSelect = (member: Visitor) => {
    if (!selectedUsers.some((selectedUser) => selectedUser.id === member.id)) {
      onChangeSelectedUsers([...selectedUsers, member]);
    }
    setFilterStr('');
    handleClose();
  };

  const handleUnselectUser = (member: Visitor) => {
    onChangeSelectedUsers(selectedUsers.filter((selectedUser) => selectedUser.id !== member.id));
    setKeyManagerIds((current) => {
      const next = new Set(current);
      next.delete(member.id);
      return next;
    });
    setExpirationByUserId((current) => {
      const next = { ...current };
      delete next[member.id];
      return next;
    });
  };

  const handleOpenExpirationDateModal = (member: Visitor) => {
    const currentExpirationDate = expirationByUserId[member.id];
    const activeGroupOption = selectedGroupOption ?? preSelectedGroup ?? null;
    openModal(
      <ExpirationDateModal
        visitorName={member.name}
        groupName={activeGroupOption?.label ?? ''}
        initialExpirationDate={currentExpirationDate ? dayjs(currentExpirationDate) : null}
        onSave={(expirationDate) =>
          setExpirationByUserId((current) => ({
            ...current,
            [member.id]: expirationDate ? expirationDate.toISOString() : null
          }))
        }
      />
    );
  };

  const handleChangeKeyManagerUser = (member: Visitor, checked: boolean) => {
    setKeyManagerIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(member.id);
      } else {
        next.delete(member.id);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!visitorGroupsLoading) {
      reset(defaultAddExistingUserFormValues);
      setKeyManagerIds(new Set());
      setExpirationByUserId({});
    }
  }, [visitorGroupsLoading, reset, defaultAddExistingUserFormValues]);

  const onSubmit = async (data: AddExistingUserFormValues) => {
    setError(undefined);
    const visitorGroupId = data.visitorGroup?.value ?? preSelectedGroup?.value;
    if (!visitorGroupId) {
      setError(translation.select_group);
      return;
    }

    const memberships: GroupMembershipInput[] = data.selectedUsers.map((selectedUser) => ({
      visitorId: selectedUser.id,
      role: keyManagerIds.has(selectedUser.id) ? Role.ExternalGroupUserManager : Role.ExternalGroupUser,
      expirationDate: expirationByUserId[selectedUser.id] ?? undefined
    }));

    try {
      const result = await addVisitorsToVisitorGroup({ visitorGroupId, memberships });

      if (result?.data) {
        showSnackbar(translation.saved_changes, { severity: 'success' });
        closeDrawer();
      } else {
        setError(translation.external_groups_user_error_adding);
      }
    } catch {
      setError(translation.external_groups_user_error_adding);
    }
  };

  return (
    <DrawerTemplate
      loading={isSubmitting}
      title={translation.add_user}
      primaryAction={{
        label: translation.add,
        onClick: handleSubmit(onSubmit),
        loading: visitorGroupsLoading || isSubmitting,
        form: FORM_ID,
        disabled: isMaxNumberOfUsersReached,
        id: 'drawer-submit-button'
      }}
      error={error}
      errorTitle={translation.could_not_add_user}
      onDismissError={() => setError(undefined)}
    >
      {visitorGroupsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Loader />
        </Box>
      ) : (
        <Stack id={FORM_ID} component="form" onSubmit={handleSubmit(onSubmit)} noValidate gap={3}>
          {topContent}
          <TextField
            id="search-input"
            variant="outlined"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        fill: filterStr !== '' ? theme.palette.text.primary : theme.palette.text.secondary
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment:
                  filterStr !== '' ? (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setFilterStr('')} aria-label={translation.clear}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined
              }
            }}
            placeholder={translation.search_user}
            onChange={(event) => setFilterStr(event.target.value)}
            value={filterStr}
            error={!!selectedUsersError}
            helperText={selectedUsersError?.message}
            sx={{ width: '100%' }}
            onFocus={(event) => setAnchorEl(event.currentTarget)}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          />
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            disableAutoFocus
            disableEnforceFocus
            sx={{}}
            slotProps={{
              paper: {
                sx: {
                  maxHeight: '320px',
                  overflowY: 'auto',
                  width: '386px'
                }
              }
            }}
          >
            <List dense>
              {membersThatMayBeManagers.length > 0 ? (
                membersThatMayBeManagers.map((member) => {
                  return (
                    <ListItemButton
                      key={member.id}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleSelect(member)}
                    >
                      <ListItemText primary={member.name} />
                    </ListItemButton>
                  );
                })
              ) : (
                <ListItemText primary={translation.no_available_user_in_role_group} sx={{ padding: '8px' }} />
              )}
            </List>
          </Popover>
          <Stack gap={0.5} sx={{ minWidth: 0 }}>
            {selectedUsers.map((selectedUser) => (
              <Stack
                key={selectedUser.id}
                padding="16px"
                sx={{ backgroundColor: theme.custom.background.level2, minWidth: 0 }}
              >
                <Stack direction="row" gap={1} sx={{ minWidth: 0 }}>
                  <Stack sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body1" noWrap title={selectedUser.name ?? undefined}>
                      {selectedUser.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" noWrap title={selectedUser.email ?? undefined}>
                      {selectedUser.email}
                    </Typography>
                  </Stack>
                  <IconButton
                    onClick={() => handleUnselectUser(selectedUser)}
                    sx={{ alignSelf: 'flex-end', flexShrink: 0 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Stack gap={0.5} sx={{ flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
                    <RepeatOverflowCounter variant="chip">
                      {getUniqueVisitorRolesWithoutInternalRolesAndExtGroupUsers(selectedUser).map((role) => (
                        <Chip
                          key={role}
                          variant="filled"
                          size="small"
                          sx={{
                            '& .MuiChip-label': {
                              fontWeight: 'medium'
                            },
                            width: 'fit-content'
                          }}
                          label={translateRole(role, translation)}
                        />
                      ))}
                    </RepeatOverflowCounter>
                    <Chip
                      id="expiration-date-chip"
                      size="small"
                      variant="outlined"
                      color={expirationByUserId[selectedUser.id] ? 'primary' : 'default'}
                      icon={<WatchLaterOutlined />}
                      label={
                        expirationByUserId[selectedUser.id]
                          ? formatDate(expirationByUserId[selectedUser.id]!)
                          : translation.set_expiration
                      }
                      onClick={() => handleOpenExpirationDateModal(selectedUser)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        id="isKeyManager-switch"
                        checked={keyManagerIds.has(selectedUser.id)}
                        onChange={(_, checked) => handleChangeKeyManagerUser(selectedUser, checked)}
                      />
                    }
                    label={translation.key_manager}
                    labelPlacement="start"
                    sx={{
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      ml: 'auto',
                      mt: '2px',
                      alignSelf: { xs: 'flex-end', sm: 'auto' }
                    }}
                  />
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </DrawerTemplate>
  );
};

export default UserFormDrawerExistingUser;
